import React, { useCallback, useEffect, useRef, useState } from "react";
import { Archive, Download, PanelRightClose, PanelRightOpen } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { downloadAssetBundleZip } from "../../../lib/assetBundleZip";

const LABELED_MIN_WIDTH = 360;

export default function ChatAssetBundleCard({
  assetBundle,
  attachment,
  uploadedAssets,
  onOpenPreview,
  highlightOpen,
  className,
}) {
  const rootRef = useRef(null);
  const [showLabels, setShowLabels] = useState(true);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const update = (width) => setShowLabels(width >= LABELED_MIN_WIDTH);
    update(el.getBoundingClientRect().width);

    const ro = new ResizeObserver(([entry]) => {
      update(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const assetCount =
    (Array.isArray(assetBundle?.assets) ? assetBundle.assets.length : 0) +
    (attachment?.data ? 1 : 0) +
    (Array.isArray(uploadedAssets) ? uploadedAssets.length : 0);
  const isDirectDownload =
    assetBundle?.downloadMode === "direct" || assetBundle?.directDownload;
  const description =
    assetBundle?.description ||
    (isDirectDownload
      ? "File ready to download"
      : `${assetCount} asset${assetCount === 1 ? "" : "s"} ready as ZIP`);
  const downloadLabel =
    assetBundle?.downloadLabel ||
    (isDirectDownload ? "Download File" : "Download All Assets");

  const [downloading, setDownloading] = useState(false);
  const PreviewIcon = highlightOpen ? PanelRightClose : PanelRightOpen;

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadAssetBundleZip({
        bundle: assetBundle,
        pdfAttachment: attachment,
        uploadedAssets,
      });
    } catch (err) {
      console.error("[ChatAssetBundleCard] ZIP download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [assetBundle, attachment, uploadedAssets, downloading]);

  if (assetBundle?.type !== "asset_bundle" || assetCount === 0) return null;

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full rounded-xl border px-3 py-2.5",
        "border-[#E85D3F]/40 bg-white text-foreground shadow-sm",
        "dark:border-[#E85D3F]/45 dark:bg-[hsl(240,4%,10%)] dark:shadow-none",
        highlightOpen && "ring-2 ring-[#E85D3F]/35 ring-offset-2 ring-offset-background dark:ring-offset-[hsl(240,4%,10%)]",
        className
      )}
      role="group"
      aria-label="Campaign asset bundle"
    >
      <div
        className={cn(
          "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-2",
          showLabels && "grid-cols-[auto_minmax(0,1fr)_auto] gap-x-3"
        )}
      >
        <div
          className={cn(
            "col-start-1 row-start-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
            "border-[#E85D3F]/35 bg-[#E85D3F]/10 text-[#B9422B]",
            "dark:border-[#E85D3F]/35 dark:bg-[#E85D3F]/10 dark:text-[#ff9a83]"
          )}
        >
          <Archive className="h-5 w-5" strokeWidth={1.75} aria-hidden />
        </div>
        <div className="col-start-2 row-start-1 min-w-0 overflow-hidden">
          <p className="truncate text-sm font-semibold text-foreground">
            {assetBundle.title || "Campaign Assets"}
          </p>
          <p className="truncate text-xs text-muted-foreground">{description}</p>
        </div>
        <div
          className={cn(
            "col-span-2 row-start-2 flex shrink-0 items-center justify-end gap-2",
            showLabels && "col-span-1 col-start-3 row-start-1"
          )}
        >
          {typeof onOpenPreview === "function" ? (
            <Button
              type="button"
              variant="outline"
              className={cn(
                "h-8 shrink-0 px-0",
                showLabels ? "w-auto px-3" : "w-8",
                "border-[#E85D3F]/45 text-[#B9422B] hover:bg-[#E85D3F]/10 dark:border-[#E85D3F]/50 dark:text-[#ff9a83]"
              )}
              onClick={onOpenPreview}
              aria-label={highlightOpen ? "Close preview" : "Open preview"}
            >
              <PreviewIcon className={cn("h-3.5 w-3.5", showLabels && "mr-1.5")} aria-hidden />
              {showLabels ? <span>{highlightOpen ? "Close" : "Preview"}</span> : null}
            </Button>
          ) : null}
          <Button
            type="button"
            className={cn(
              "relative h-8 shrink-0 px-0",
              showLabels ? "w-auto px-3" : "w-8",
              "bg-[#E85D3F] text-white hover:bg-[#cf4f34]"
            )}
            disabled={downloading}
            aria-busy={downloading}
            aria-label={downloadLabel}
            onClick={handleDownload}
          >
            <span className={cn("inline-flex items-center justify-center", downloading && "invisible")}>
              <Download className={cn("h-3.5 w-3.5", showLabels && "mr-1.5")} aria-hidden />
              {showLabels ? <span>{downloadLabel}</span> : null}
            </span>
            {downloading ? (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <LoadingSpinner />
              </span>
            ) : null}
          </Button>
        </div>
      </div>
    </div>
  );
}
